import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetEMSMemberEnergyQuery,
  useGetEMSSetpointsQuery,
} from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

/* Chart 3 — solar usage: community solar available vs. how much the batteries
   absorb. The optimizer stores the midday surplus instead of exporting it. */
function PromoterSolarAllocChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: rawList = [], isLoading } = useGetEMSMemberEnergyQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 5000, offset: 0 },
    { skip: !communityId }
  );
  const { data: setpointsResp } = useGetEMSSetpointsQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 20000, offset: 0 },
    { skip: !communityId }
  );
  // the setpoints endpoint returns { data: [...], total_count } (not a bare array)
  const setpoints = useMemo(
    () => (Array.isArray(setpointsResp?.data) ? setpointsResp.data : []),
    [setpointsResp]
  );
  // Storage resource ids straight from the soc_bat keys (always present in the
  // member-energy metadata) — more reliable than re-deriving from the resources
  // query, which can return without a usable `type`.
  const storageIds = useMemo(() => {
    const s = new Set();
    rawList.forEach((r) =>
      Object.keys(r.metadata?.soc_bat || {}).forEach((id) => s.add(String(id)))
    );
    return s;
  }, [rawList]);

  const hasData = useMemo(() => rawList.some((r) => r.metadata?.e_avail != null), [rawList]);
  const isEmpty = !isLoading && !hasData;
  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (!hasData) return { series: [], labels: [] };
    // member-energy and setpoints come from different endpoints that serialize
    // the timestamp slightly differently — key everything by epoch ms so the two
    // sources line up (otherwise the charge lookup misses and reads 0).
    const ms = (ts) => new Date(ts).getTime();
    const avail = new Map(); // ms -> kWh
    const tsByMs = new Map(); // ms -> original ts (for the label)
    rawList.forEach((r) => {
      const ts = r.datetime_timestamp;
      if (!ts) return;
      const k = ms(ts);
      if (!tsByMs.has(k)) tsByMs.set(k, ts);
      if (r.metadata?.e_avail != null && !avail.has(k)) {
        avail.set(k, Number(Number(r.metadata.e_avail).toFixed(3)));
      }
    });
    const chg = new Map(); // ms -> kWh
    setpoints.forEach((sp) => {
      if (sp.setpoint_type !== "CONSUME" || !storageIds.has(String(sp.resource_id))) return;
      const ts = sp.datetime_timestamp;
      if (!ts) return;
      const k = ms(ts);
      chg.set(k, (chg.get(k) || 0) + Math.abs(sp.setpoint_value || 0));
    });
    const sorted = [...tsByMs.keys()].sort((a, b) => a - b);
    return {
      labels: sorted.map((k) => createChartXAxisLabel(tsByMs.get(k), "MIN15")),
      series: [
        {
          name: t("unified_ems.promoter_solar_avail"),
          data: sorted.map((ts) => avail.get(ts) ?? 0),
          color: "var(--warning)",
        },
        {
          name: t("unified_ems.battery_charge"),
          data: sorted.map((ts) => Number((chg.get(ts) || 0).toFixed(3))),
          color: "var(--success)",
        },
      ],
    };
  }, [rawList, setpoints, storageIds, hasData, t]);

  if (isLoading || isEmpty) return null;
  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.promoter_solar_alloc_chart_title")}
      sub={t("unified_ems.promoter_solar_alloc_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      height={420}
      unit="kWh"
      yTitle="kWh"
      type="area"
    />
  );
}

PromoterSolarAllocChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};
PromoterSolarAllocChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default PromoterSolarAllocChart;
